/**
 * Alerta inline para janela de 24h expirada
 * 
 * Exibido acima da área de input quando a janela de 24h do WhatsApp Oficial
 * está expirada, informando que apenas templates podem ser enviados.
 * 
 * Suporta prop `disabled` para desabilitar o botão até o agente assumir a conversa.
 */

import { AlertTriangle, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface SessionWindowAlertProps {
  /** Callback para abrir modal de seleção de template */
  onSendTemplate: () => void;
  /** Desabilita o botão até o agente assumir a conversa */
  disabled?: boolean;
}

export function SessionWindowAlert({ onSendTemplate, disabled }: SessionWindowAlertProps) {
  const content = (
    <div className="flex items-center justify-between gap-3 px-4 py-2 mb-2 rounded-lg bg-destructive/10 border border-destructive/20">
      <div className="flex items-center gap-2 text-sm text-destructive">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
        <span>
          Janela de 24h expirada. Envie um{' '}
          <button
            onClick={disabled ? undefined : onSendTemplate}
            disabled={disabled}
            className={cn(
              "font-medium underline hover:no-underline",
              disabled && "opacity-50 cursor-not-allowed no-underline"
            )}
          >
            template
          </button>
          {' '}para continuar.
        </span>
      </div>
      <Button
        variant="destructive"
        size="sm"
        onClick={onSendTemplate}
        disabled={disabled}
        className="flex-shrink-0"
      >
        <FileText className="h-4 w-4 mr-1" />
        Enviar Template
      </Button>
    </div>
  );

  // Se desabilitado, envolve em Tooltip explicativo
  if (disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div>{content}</div>
        </TooltipTrigger>
        <TooltipContent>
          Clique em "Assumir" primeiro para enviar template
        </TooltipContent>
      </Tooltip>
    );
  }

  return content;
}
